import { useState } from "react";
import { Link } from "react-router-dom";
import {
  selectAllCollections,
  selectCollectionIdsByRecipeId,
  setRecipeCollections,
  useT,
  useAppDispatch,
  useAppSelector,
  type RootState,
} from "@reciperoll/core";
import { Check } from "react-feather";
import { useToast } from "./Toast";

// Pick which collections this recipe belongs to (checkbox list, pre-checked from
// current membership). The reverse of the collection's "Add recipes" modal —
// saving replaces this recipe's collection set via setRecipeCollections.
export function AddToCollectionModal({
  recipeId,
  onClose,
}: {
  recipeId: string;
  onClose: () => void;
}) {
  const t = useT();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const collections = useAppSelector(selectAllCollections);
  const current = useAppSelector((s: RootState) => selectCollectionIdsByRecipeId(s, recipeId));

  const [selected, setSelected] = useState<Set<string>>(() => new Set(current));

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const save = () => {
    dispatch(setRecipeCollections({ recipeId, collectionIds: [...selected] }));
    toast.show(
      selected.size === 0
        ? t("collections.removedFromAll")
        : t("collections.savedToCount", { count: selected.size }),
    );
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md max-h-[80vh] flex flex-col rounded-2xl bg-surface p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold text-ink mb-3">{t("recipe.addToCollection")}</h2>

        {collections.length === 0 ? (
          <div className="py-6 text-center">
            <p className="text-sm text-ink-soft mb-3">{t("collections.noCollectionsYet")}</p>
            <Link
              to="/collections"
              onClick={onClose}
              className="text-sm font-semibold text-terracotta hover:text-terracotta-dark"
            >
              {t("collections.createCollection")}
            </Link>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto -mx-1 pr-1">
            {collections.map((c) => {
              const checked = selected.has(c.id);
              return (
                <button
                  key={c.id}
                  onClick={() => toggle(c.id)}
                  className="w-full flex items-center gap-3 px-1 py-2.5 text-left rounded-lg hover:bg-surface-2 cursor-pointer"
                >
                  <div
                    className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${
                      checked ? "bg-terracotta border-terracotta" : "border-line"
                    }`}
                  >
                    {checked && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                  </div>
                  <span className="text-sm font-medium text-ink truncate">{c.name}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-4 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 rounded-xl border border-line py-2.5 font-semibold text-ink-soft hover:bg-surface-2 cursor-pointer"
          >
            {t("common.cancel")}
          </button>
          {collections.length > 0 && (
            <button
              onClick={save}
              className="flex-1 rounded-xl bg-terracotta hover:bg-terracotta-dark transition-colors py-2.5 font-semibold text-white cursor-pointer"
            >
              {t("common.save")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
